import forEach from 'lodash/forEach';
import identity from 'lodash/identity';
import isString from 'lodash/isString';

import { toUnderscore } from './string_utils';

const encode = encodeURIComponent;
const strip = (string, regex) => string.replace(regex, '');

function getQueryString(url) {
  const query = url.split('?')[1];
  return query || '';
}

export function getQueryParam(url, param) {
  if (URLSearchParams) {
    const urlParams = new URLSearchParams(getQueryString(url));
    if (param.includes('[]')) {
      return urlParams.getAll(param);
    }
    return urlParams.get(param);
  }
  // From: https://davidwalsh.name/query-string-javascript
  param = param.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + param + '=([^&#]*)');
  const results = regex.exec(url);
  return results === null
    ? ''
    : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

export function simpleObjectSerializer(
  params = {},
  keepNull,
  enableMultiValueArray,
) {
  const uri = [];
  forEach(params, (value, key) => {
    if (!keepNull && !value) return;
    if (enableMultiValueArray && Array.isArray(value)) {
      value.map(val =>
        uri.push(encode(toUnderscore(key)) + '[]=' + encode(val)),
      );
    } else {
      uri.push(encode(toUnderscore(key)) + '=' + encode(value));
    }
  });
  return uri.join('&');
}

function setParam(url, key, value) {
  const PARAM_REGEX = new RegExp('([?&])' + key + '=.*?(&|$)', 'i');
  const separator = url.includes('?') ? '&' : '?';
  if (url.match(PARAM_REGEX)) {
    return url.replace(PARAM_REGEX, '$1' + key + '=' + value + '$2');
  }
  return url + separator + key + '=' + value;
}

export function setParamsFromObject(url, obj) {
  return Object.entries(obj).reduce((buildUrl, [key, value]) => {
    if (value) {
      return setParam(buildUrl, key, value);
    }
    return buildUrl;
  }, url);
}

export function updateQueryString(url, queryString) {
  let result = strip(url, /(\/|\?.+)$/);
  const strippedQueryString = strip(queryString, /^\?/);
  const params = [
    ...getQueryString(url).split('&'),
    ...strippedQueryString.split('&'),
  ];
  params.filter(identity).forEach(param => {
    result = setParam(result, ...param.split('='));
  });
  return result;
}

export function replaceQueryString(url, queryString) {
  const strippedUrl = strip(url, /(\/|\?.+)$/);
  return updateQueryString(strippedUrl, queryString);
}

export function setQueryString(
  url,
  query,
  {
    replaceState = false,
    merge = false,
    keepNull = true,
    enableMultiValueArray = false,
  } = {},
) {
  const queryString = isString(query)
    ? query
    : simpleObjectSerializer(query, keepNull, enableMultiValueArray);
  const newUrl = merge
    ? updateQueryString(url, queryString)
    : replaceQueryString(url, queryString);
  if (replaceState) {
    return window.history.replaceState({}, null, newUrl);
  }
  return newUrl;
}

export function setQueryStringForCurrentUrl(query, options = {}) {
  setQueryString(window.location.pathname, query, {
    replaceState: true,
    ...options,
  });
}

const prefixIfPresent = str => (str ? `?${str}` : '');

const paramsToQueryStringRecursive = (...params) => {
  if (!params) return '';
  return params
    .map(param => {
      if (!param) return '';
      const paramType = typeof param;
      if (paramType === 'string' || paramType === 'number') return param;
      if (Array.isArray(param) && param.length) {
        return paramsToQueryStringRecursive(...param);
      }
      if (paramType === 'object') {
        return Object.keys(param)
          .map(key => (param[key] ? `${key}=${param[key]}` : ''))
          .filter(Boolean)
          .join('&');
      }
      return '';
    })
    .filter(Boolean)
    .join('&');
};

export const paramsToQueryString = (...args) =>
  prefixIfPresent(paramsToQueryStringRecursive(...args));

export const urlIsZiggeo = url => {
  if (!url) return null;
  const regex = /https:\/\/ziggeo\.io\/p\/(\w+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};
